import { injectFakePagination } from '../../Common/Helper/Pagination';
import { useNavigate, useParams } from 'react-router';
import { useCallback, useMemo, useState } from 'react';
import { Add, Bookmark, Delete, Edit } from '@mui/icons-material';
import { BlueprintPanel } from './BlueprintPanel';
import Permissions from 'src/Common/Permissions';
import { Blueprint, Pagination, PaginationResponse, Zone } from '@ekkogmbh/apisdk';
import { DataTable } from 'src/Common/Components/DataTable';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { BlueprintStepsDialog } from './BlueprintStepsDialog';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { useContentControl } from 'src/Common/Providers/ContentControlProvider';

export const BlueprintManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { scrollTop } = useNavigation();
  const navigate = useNavigate();
  const { refresh } = usePagination();
  const { setPanelOpen } = useContentControl();

  const pathParams = useParams();
  const [, startLoading] = useRequest();

  const [openDialog, setOpenDialog] = useState<'steps' | 'delete' | undefined>();
  const [selectedBlueprint, setSelectedBlueprint] = useState<Blueprint | undefined>();

  const zoneIdentifier: Pick<Zone, 'name' | 'coordinate'> | undefined = useMemo(() => {
    const { zoneName, coordinate } = pathParams;

    if (zoneName && coordinate) {
      return { name: zoneName, coordinate };
    }

    return undefined;
  }, [pathParams]);

  const fetchItems = useCallback(
    async (pagination: Pagination) => {
      if (!zoneIdentifier) {
        return { currentPage: 0, items: [], pageCount: 0, itemsPerPage: 0, totalItemCount: 0 };
      }

      const { name, coordinate } = zoneIdentifier;
      const blueprints = await api.getBlueprints(coordinate, name, pagination);

      if ((blueprints as PaginationResponse<Blueprint>).totalItemCount !== undefined) {
        return blueprints as PaginationResponse<Blueprint>;
      }

      return injectFakePagination<Blueprint>(blueprints as Blueprint[]);
    },
    [api, zoneIdentifier],
  );

  const onDismiss = () => {
    setPanelOpen(false);
    setSelectedBlueprint(undefined);
    setOpenDialog(undefined);
  };

  const onEdit = useCallback((blueprint?: Blueprint) => {
    onDismiss();
    setSelectedBlueprint(blueprint);
    setPanelOpen(true);
    scrollTop();
  }, []);

  const onSteps = (blueprint: Blueprint) => {
    setSelectedBlueprint(blueprint);
    setOpenDialog('steps');
    setPanelOpen(false);
  };

  const onDeleteOk = useCallback(async () => {
    if (selectedBlueprint === undefined) {
      return;
    }

    startLoading(
      () => api.deleteBlueprint(selectedBlueprint),
      () => {
        onDismiss();
        refresh();
      },
    );
  }, [api, selectedBlueprint]);

  const dialog = useMemo(() => {
    if (selectedBlueprint === undefined) {
      return;
    }

    switch (openDialog) {
      case 'delete':
        return (
          <GenericDialog
            open
            fullWidth
            centered
            type={'confirmation'}
            maxWidth={'sm'}
            title={'Delete Blueprint'}
            text={selectedBlueprint.name}
            onClose={onDismiss}
            onConfirm={onDeleteOk}
          />
        );
      case 'steps':
        return <BlueprintStepsDialog blueprint={selectedBlueprint} onClose={onDismiss} />;
      default:
        return;
    }
  }, [openDialog, selectedBlueprint, onDeleteOk]);

  const buttons = useMemo(
    () => [
      <ContentControlButton
        key={'add'}
        content={<Add />}
        tooltip={'Add Blueprint'}
        onClick={() => {
          onEdit(undefined);
        }}
        requiredPermission={Permissions.BLUEPRINTS_WRITE}
      />,
    ],
    [],
  );

  return (
    <ContentControl
      panel={<BlueprintPanel blueprint={selectedBlueprint} closeHandler={onDismiss} zoneIdentifier={zoneIdentifier} />}
      dialog={dialog}
      buttons={buttons}
    >
      <DataTable
        title={zoneIdentifier ? zoneIdentifier.name + ' @ ' + zoneIdentifier.coordinate : ''}
        onBack={() => navigate('/picking/zones')}
        fetchItems={fetchItems}
        columns={[
          {
            header: 'Name',
            body: (entry: Blueprint) => entry.name,
            filterField: 'name',
            sortField: 'BP.name',
          },
          {
            header: 'Once',
            body: (entry: Blueprint) => String(entry.once),
          },
        ]}
        actions={[
          {
            title: 'Show Steps',
            icon: <Bookmark />,
            onClick: (entry: Blueprint) => onSteps(entry),
          },
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: Blueprint) => onEdit(entry),
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: Blueprint) => {
              setSelectedBlueprint(entry);
              setOpenDialog('delete');
            },
          },
        ]}
      />
    </ContentControl>
  );
};
